import { Component, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { CalendarView, XnFormControlComponent, XnFormControlValidationType, XnInputType } from '../xn-form-control/xn-form-control.component';

export interface XnFormChangedEvent{
  name: string, 
  value: string, 
  values: any
}

interface XnFormFieldItem{
  name: string,
  label?: string,
  placeholder?: string,
  required?: boolean,
  type?: XnInputType,
  minDate?: Date,
  maxDate?: Date,
  dateFormat?: string,
  calendarView?: CalendarView,
  optionLabel?: string,
  optionValue?: string,
  selectOptions?: any[],
  translateOptions?: boolean,
  valid?: boolean,
  errors?: string[]
  customValidateError?: (value: any, data: any) => string
  onChanged?: (event: XnFormChangedEvent) => void
  autocomplete?: 'on' | 'off' | 'new-password',
  passwordToggleMask?: boolean,
  passwordFeedback?: boolean,
  value?: any,
  readonly?: boolean,
  canShow?: (field: any, values: any) => boolean,
  fileHint?: string,
  fileHintHtml?: string,
  fileHintHtmlTranslate?: string,
  fileButtonText?: string,
  fileAccept?: string,
  fileMaxSizeInMb?: number // mb
  disabled?: boolean,
  hideLabel?: boolean,
  removed?: boolean,
  validationType?: XnFormControlValidationType
  maxLength?: number,
  minLength?: number,
  hidePasswordHints?: boolean,
}

interface XnFormFieldColumn extends XnFormFieldItem{

}

export interface XnFormField extends XnFormFieldItem{
  columns?: XnFormFieldColumn[]
  rowClass?: string
}

export function convertArrayFieldsToColumns(fields: XnFormField[], numCols: number): XnFormField[]{
  if(!fields || !fields.length) return [];
  if(numCols < 2) return fields;

  const total = fields.length; 
  const totalRows = Math.floor(total / numCols) + ((total % numCols) ? 1 : 0);

  const rows: XnFormField[] = [];

  for(let i = 0; i < totalRows; i++){
    const row: XnFormField = {
      name: `row-${i+1}`,
      type: 'row',
      columns: []
    };

    const columns = fields.slice(i * numCols, (i + 1) * numCols);
    if(columns.length < numCols){
      const more = numCols - columns.length;
      for(let j = 0; j < more; j++){
        columns.push({
          type: 'empty',
          name: `blank-${i}${j}`
        })
      }
    }

    row.columns = columns;

    rows.push(row);
  }

  return rows;
}

@Component({
  selector: 'app-xn-form',
  standalone: false,
  templateUrl: './xn-form.component.html',
  styleUrls: ['./xn-form.component.sass']
})
export class XnFormComponent implements OnInit {

  @Input('fields') fields: XnFormField[] = []
  @Input('submitted') submitted: boolean = false;
  @Input('displayType') displayType: 'normal' | 'float' = 'normal';
  @Input('styleClass') styleClass?: string
  values: {[key: string]: any} = {};

  @ViewChildren('xnControl') xnControls? :QueryList<XnFormControlComponent>;

  private _formId?: string
  setFormId(id: string){
    this._formId = id;
  }

  getFormId(){
    return this._formId;
  }
  
  errors: string[] = [];

  constructor() { }

  

  ngOnInit(): void {
    this.applyValues();
  }

  applyValues(){
    const flatFields = this.getFlatFields();
    flatFields.forEach(f => {
      if(f.value !== undefined){
        this.values[f.name] = f.value;
      }
    })
  }

  getData(){
    const results: {[key: string]: any} = {};
    const flatFields = this.getFlatFields();

    flatFields.forEach(f => {
      if(this.canShow(f)){
        if(this.values[f.name] !== undefined){
          results[f.name] = this.values[f.name];

          if(f.type === 'phone'){
            results[`${f.name}DialCode`] = this.extractCountryCode(this.values[f.name]);
            results[f.name] = this.removeParenthesesContent(this.values[f.name]);
          }
        }
      }
    })
    return results;
  }

  extractCountryCode(number: string): string | null {
    const regex = /^\(\+(\d+)\)/;
    const match = number.match(regex);
    return match ? `+${match[1]}` : null;
  }

  removeParenthesesContent(number: string): string {
    return number.replace(/\(.*?\)/g, '');
  }

  isValid(): boolean{
    const flatFields = this.getFlatFields().filter(f => {
      return this.canShow(f);
    });
    const invalid = flatFields.some(f => !f.valid);
    this.errors = [];
    flatFields.forEach(f => {
      if(!f.valid && f.errors && f.errors.length){
        this.errors.push(f.errors[0]);
      }
    })
    return !invalid;
  }

  getErrors(){
    if(this.isValid()) return [];

    return this.errors;
  }

  getFlatFields(): XnFormField[]{
    const flatFields: XnFormField[] = [];
    this.fields.forEach(f => {
      if(f.type === 'row'){
        if(f.columns && f.columns.length){
          f.columns.forEach(c => {
            if(c.type !== 'empty' && c.type !== 'line'){
              flatFields.push(c);
            }
          })
        }
      }
      else if(f.type !== 'empty' && f.type !== 'line'){
        flatFields.push(f);
      }
    })
    return flatFields;
  }

  onFieldStateChanged(event: any){
    if(event && event.name){
      const flatFields = this.getFlatFields();
      const field = flatFields.find(f => f.name === event.name);
      if(field){
        field.valid = event.valid;
        if(!field.valid){
          field.errors = event.errors;
        }
      }
    }
  }

  getFieldType(field: XnFormField){
    return field.type || 'text';
  }

  populate(data: any){
    if(data){
      for(let key in data){
        this.values[key] = data[key];
      }
    }

    this.$afterPopulated.next({form: this, data: data})
  }

  private $afterPopulated = new Subject<any>();
  afterPopulated(): Observable<any>{
    return this.$afterPopulated.asObservable();
  }

  getField(name: string){
    const fields = this.getFlatFields();

    const field = fields.find(e => e.name === name);
    return field;
  }

  setSelectOptions(name: string, options: any){
    const field = this.getField(name);
    if(!field) return;

    field.selectOptions = options;
  }

  getSelectedOption(fieldName: string){
    const field = this.getField(fieldName);
    if(!field) return null;

    const value = this.values[fieldName];

    if(!value || !field.selectOptions || !field.selectOptions.length) return null;

    return field.selectOptions.find(e => field.optionValue && e[field.optionValue] === value)
  }

  setValue(name: string, value: any){
    const field = this.getField(name);
    if(!field) return;
    this.values[name] = value;
  }

  clear(){
    const flatFields = this.getFlatFields();
    flatFields.forEach(f => {
      if(f.type === 'checkbox'){
        this.values[f.name] = false;
      }
      else if(f.type === 'select'){
        this.values[f.name] = '';
      }
      else{
        this.values[f.name] = '';
      }
    })
  }

  reset(){
    this.clear();
    if(this.xnControls && this.xnControls.length){
      this.xnControls.forEach(xnControl => {
        xnControl.reset();
      })
    }
  }

  canShow(field: XnFormField){
    if(field.removed) return false;
    if(field.canShow && typeof field.canShow === 'function'){
      return field.canShow(field, this.values);
    }
    return true;
  }

  getCanShowField(){
    const returnField = this.fields.filter(field => {
      if (field.columns && field.columns.length) {
        return field.columns.some(col => {
          if(col.canShow && typeof col.canShow === 'function'){
            return col.canShow(col, this.values);
          }
          return true;
        })
      }
      return true;
    })

    return returnField;
  }

  disableField(name: string){
    const field = this.getField(name);
    if(!field) return;
    console.log(`disableField: ${name}`)
    field.disabled = true;
  }

  disableFields(names: string[]){
    if(!names || !names.length) return;
    names.forEach(e => {
      this.disableField(e);
    })
  }

  enableField(name: string){
    const field = this.getField(name);
    if(!field) return;
    field.disabled = false;
  }

  enableFields(names: string[]){
    if(!names || !names.length) return;
    names.forEach(e => {
      this.enableField(e);
    })
  }

  resetControl(name: string){
    if(!this.xnControls || !this.xnControls.length){
      return;
    }
    const control = this.xnControls.find(e => e.name === name);
    if(control){
      control.reset();
    }
  }
}
